import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Pagination,
  PaginationItem,
  Tooltip,
} from "@mui/material";
import LoadingSpinner from "../../components/LoadingSpinner";
import ReactModal from "react-modal";
import AssigneeBulkTeam from "./inboxEmailModal/AssigneeBulkTeam";
import { toast } from "react-toastify";
import ConfirmationModal from "../reusableComponent/NonActionableConfirmationModal";


import "./EmailThreadList.scss";

interface EmailThreadListProps {
  useThreadsQuery: any;
  useUpdateStarredMutation: any;
  useUpdateNonActionableMutation: any;
  useUpdateActionableMutation: any;
  FilterModalComponent: React.ElementType;
  filterSessionKey: string;
  sessionKeyPrefix: string;
  title: string;
  mobileNumber?: string;
  callActive?: boolean;
  filterKey?: string;
}

const isMobile = () => window.innerWidth <= 768;

const customFilterModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "650px",
    padding: "0",
    border: "none",
    borderRadius: "16px",
    overflow: "hidden",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
  },
};

const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "650px",
    padding: "0",
    border: "none",
    borderRadius: "16px",
    overflow: "hidden",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
  },
};

const EmailThreadList: React.FC<EmailThreadListProps> = ({
  useThreadsQuery,
  useUpdateStarredMutation,
  useUpdateNonActionableMutation,
  useUpdateActionableMutation,
  FilterModalComponent,
  filterSessionKey,
  sessionKeyPrefix,
  title,
  mobileNumber,
  callActive = false,
  filterKey = "selectedFilter1",
}) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [allThreads, setAllThreads] = useState<any[]>([]);
  const [countAllThreads, setCountAllThreads] = useState<number>(0);

  // Confirmation modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<{
    id: any;
    isNonActionable: boolean;
  } | null>(null);

  const parseSessionValue = (value: string | null) => {
    if (!isNaN(Number(value))) return Number(value);
    return value;
  };

  const pageSizeOptions = [5, 10, 20, 30, 50];

  const [pageSize, setPageSize] = useState<number>(
    parseInt(sessionStorage.getItem(`${sessionKeyPrefix}perPage`) || "10", 10),
  );

  const [selectedTeamId, setSelectedTeamId] = useState<any>(() => {
    const selectedTeamIdFromSession = sessionStorage.getItem(
      `${sessionKeyPrefix}selectedTeamId`,
    );
    const userRole = localStorage.getItem("user_role");
    const userId = localStorage.getItem("User_Id");
    if (selectedTeamIdFromSession)
      return parseSessionValue(selectedTeamIdFromSession);
    if (userRole === "admin") return "My Team";
    if (userRole === "agent" || userRole === "manager") return userId;
    return null;
  });

  const [currentPage, setCurrentPage] = useState<number>(
    parseInt(
      sessionStorage.getItem(`${sessionKeyPrefix}currentPage`) || "1",
      10,
    ),
  );

  const [selectedFilter, setSelectedFilter] = useState<any>({
    action__contact__email__icontains: "",
    action__assignee__username__icontains: "",
    original_subject__icontains: "",
    created: "",
    action__priority__icontains: "",
  });

  const [selectedFilter1, setSelectedFilter1] = useState<any>(() => {
    const sessionData = sessionStorage.getItem(filterSessionKey);
    return sessionData && sessionData !== "undefined"
      ? JSON.parse(sessionData)
      : {
          action__contact__email__icontains: "",
          action__assignee__username__icontains: "",
          original_subject__icontains: "",
          created: "",
          action__priority__icontains: "",
        };
  });

  const queryArgs: any = {
    currentPage,
    perPage: pageSize,
    selectedTeamId,
    mobileNumber,
  };
  queryArgs[filterKey] = selectedFilter1;

  const {
    data: allThreadsData,
    isLoading,
    refetch: refetchAllThreads,
    status,
  } = useThreadsQuery(queryArgs, { skip: false });

  const [trigger] = useUpdateStarredMutation();
  const [triggerNonActionable] = useUpdateNonActionableMutation();
  const [triggerActionable] = useUpdateActionableMutation();
  const [isLoadingPageChange, setIsLoadingPageChange] =
    useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);

  const openFilterModal = () => setIsFilterModalOpen(true);
  const closeFilterModal = () => setIsFilterModalOpen(false);

  const totalPages = Math.ceil(countAllThreads / pageSize);

  useEffect(() => {
    if (refetch) {
      refetchAllThreads();
      setRefetch(false);
    }
  }, [refetch, refetchAllThreads]);

  useEffect(() => {
    setRefetch(true);
  }, []);

  useEffect(() => {
    if (allThreadsData?.results) {
      setAllThreads(allThreadsData.results);
      setCountAllThreads(allThreadsData.count);
    } else {
      setAllThreads([]);
      setCountAllThreads(0);
    }
  }, [allThreadsData]);

  useEffect(() => {
    if (status === "pending") setIsLoadingPageChange(true);
    else setIsLoadingPageChange(false);
  }, [status]);

  useEffect(() => {
    sessionStorage.setItem(`${sessionKeyPrefix}selectedTeamId`, selectedTeamId);
    sessionStorage.setItem(`${sessionKeyPrefix}currentPage`, "1");
    setCurrentPage(1);
    refetchAllThreads();
  }, [selectedTeamId]);

  useEffect(() => {
    sessionStorage.setItem(`${sessionKeyPrefix}perPage`, pageSize.toString());
    sessionStorage.setItem(
      `${sessionKeyPrefix}currentPage`,
      currentPage.toString(),
    );
    refetchAllThreads();
  }, [pageSize, currentPage]);

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };

  const handleHeaderCheckboxChange = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      setSelectedRows(allThreads.map((thread) => String(thread.id)));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = e.target.value;
    if (e.target.checked) {
      setSelectedRows((prev) => [...prev, selectedId]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== selectedId));
      setSelectAll(false);
    }
  };

  const handleSubmit = (filterValues: any) => {
    setSelectedFilter1(filterValues);
    sessionStorage.setItem(filterSessionKey, JSON.stringify(filterValues));
  };

  const getInitials = (name: string): string => {
    if (!name) return "";
    const nameParts = name
      .trim()
      .split(/[\s\/-]+/)
      .filter(Boolean);
    return nameParts
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("");
  };

  const convertHtmlIntoText = (htmlContent: string): string => {
    if (!htmlContent) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    return truncateText(doc.body.textContent || "", 100);
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const mailSentOrReceivedDate = (mailDate: string | undefined): string => {
    if (!mailDate) return "";
    const date = new Date(mailDate);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const emailDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    if (emailDay.getTime() === today.getTime()) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    if (emailDay.getTime() === yesterday.getTime()) {
      return "Yesterday";
    }
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const handleTeamChange = (e: any) => {
    setSelectedTeamId(e.target.value);
    setCurrentPage(1);
  };
  const handleRefreshClick = () => refetchAllThreads();
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const assigneBulkEmailHandler = () => {
    if (selectedRows.length === 0)
      return toast.error("Please select at least one email.");
    openModal();
  };

  const capitalizeFirstLetter = (name: string): string => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleStarClick = async (id: any, isStarred: boolean) => {
    try {
      const response = await trigger({ body: { is_starred: !isStarred }, id });
      if (response?.data) refetchAllThreads();
      else if (response?.error) toast.error("Unable to update");
    } catch {
      toast.error("An error occurred");
    }
  };

  // Actual API call — runs only after user confirms
  const handleNonActionableClick = async (
    id: any,
    isNonActionable: boolean,
  ) => {
    try {
      const response = isNonActionable
        ? await triggerActionable(id)
        : await triggerNonActionable({
            body: { is_non_actionable: !isNonActionable },
            id,
          });

      if (response?.data) {
        refetchAllThreads();
        toast.success(
          isNonActionable
            ? "Email marked as actionable"
            : "Email marked as non-actionable",
        );
      } else if (response?.error) {
        toast.error("Unable to update non-actionable status");
      }
    } catch {
      toast.error("An error occurred while updating non-actionable status");
    }
  };

  // Fires after user clicks "Confirm" in the ConfirmationModal
  const handleConfirmNonActionable = () => {
    if (confirmPayload) {
      handleNonActionableClick(
        confirmPayload.id,
        confirmPayload.isNonActionable,
      );
    }
    setConfirmPayload(null);
  };

  const decodeMimeWords = (input?: string): string => {
    if (!input) return "";
    const normalized = input.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();
    return normalized.replace(
      /=\?([^?]+)\?([bBqQ])\?([^?]+)\?=/g,
      (_, charset, encoding, text) => {
        try {
          const enc = encoding.toUpperCase();
          const decoder = new TextDecoder(charset.toLowerCase());
          if (enc === "B") {
            const binary = atob(text.replace(/\s/g, ""));
            const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
            return decoder.decode(bytes);
          } else {
            const cleaned = text.replace(/_/g, " ");
            const bytes: number[] = [];
            for (let i = 0; i < cleaned.length; i++) {
              const ch = cleaned[i];
              if (
                ch === "=" &&
                i + 2 < cleaned.length &&
                /[0-9A-Fa-f]{2}/.test(cleaned.slice(i + 1, i + 3))
              ) {
                bytes.push(parseInt(cleaned.slice(i + 1, i + 3), 16));
                i += 2;
              } else {
                bytes.push(ch.charCodeAt(0));
              }
            }
            return decoder.decode(new Uint8Array(bytes));
          }
        } catch {
          return text;
        }
      },
    );
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      { bg: "#E0F2FE", text: "#6366f1" },
      { bg: "#E0E7FF", text: "#6366f1" },
      { bg: "#FCE7F3", text: "#DB2777" },
      { bg: "#FEF3C7", text: "#D97706" },
      { bg: "#D1FAE5", text: "#059669" },
      { bg: "#E5E7EB", text: "#6B7280" },
    ];
    const charCode = name.charCodeAt(0);
    return colors[charCode % colors.length];
  };

  const [viewMode, setViewMode] = useState<"mobile" | "desktop">(
    isMobile() ? "mobile" : "desktop",
  );

  const emailReturnKey = useMemo(() => {
    const match = sessionKeyPrefix.match(/^(inbox|starred|sent)Initial_$/);
    return match ? `email_return_${match[1]}` : null;
  }, [sessionKeyPrefix]);

  const hasScrolledToReturnRef = useRef(false);

  const saveReturnInfo = useCallback(
    (ticketId: string | number | undefined, subject: string | undefined) => {
      if (!emailReturnKey) return;
      const payload = JSON.stringify({
        ticketId: ticketId != null ? String(ticketId) : "",
        subject: subject != null ? String(subject) : "",
      });
      sessionStorage.setItem(emailReturnKey, payload);
    },
    [emailReturnKey],
  );

  const listReady = !isLoading && !isLoadingPageChange && allThreads.length > 0;

  useLayoutEffect(() => {
    if (!emailReturnKey || !listReady || hasScrolledToReturnRef.current) return;
    const raw = sessionStorage.getItem(emailReturnKey);
    if (!raw) return;
    let ticketId = "",
      subject = "";
    try {
      const parsed = JSON.parse(raw);
      ticketId = parsed?.ticketId ?? "";
      subject = parsed?.subject ?? "";
    } catch {
      sessionStorage.removeItem(emailReturnKey);
      return;
    }
    if (!ticketId && !subject) {
      sessionStorage.removeItem(emailReturnKey);
      return;
    }

    const scrollToRow = (): boolean => {
      if (ticketId) {
        const rows = document.querySelectorAll("[data-email-ticket-id]");
        for (let i = 0; i < rows.length; i++) {
          if (rows[i].getAttribute("data-email-ticket-id") === ticketId) {
            rows[i].scrollIntoView({ block: "nearest", behavior: "auto" });
            return true;
          }
        }
      }
      if (subject) {
        const subjectNorm = subject.slice(0, 200);
        const rows = document.querySelectorAll("[data-email-subject]");
        for (let i = 0; i < rows.length; i++) {
          const attr = rows[i].getAttribute("data-email-subject") || "";
          if (
            attr === subjectNorm ||
            (attr.length > 15 &&
              subjectNorm.length > 15 &&
              attr.slice(0, 50) === subjectNorm.slice(0, 50))
          ) {
            rows[i].scrollIntoView({ block: "nearest", behavior: "auto" });
            return true;
          }
        }
      }
      return false;
    };

    const id = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const didScroll = scrollToRow();
          if (didScroll) {
            hasScrolledToReturnRef.current = true;
            sessionStorage.removeItem(emailReturnKey);
          }
        });
      });
    }, 150);
    return () => clearTimeout(id);
  }, [emailReturnKey, listReady, allThreads]);

  useEffect(() => {
    if (!emailReturnKey) return;
    return () => {
      const mainContent = document.getElementById("kt_content");
      if (mainContent) mainContent.scrollTop = 0;
      window.scrollTo(0, 0);
    };
  }, [emailReturnKey]);

  useEffect(() => {
    const handleResize = () => setViewMode(isMobile() ? "mobile" : "desktop");
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Shared non-actionable icon renderer ──────────────────────────────────
  // If already non-actionable → mark actionable via ConfirmationModal
  // If not yet non-actionable → mark non-actionable via ConfirmationModal
  const renderNonActionableBtn = (
    details: any,
    extraStyle?: React.CSSProperties,
  ) => {
    const alreadyNonActionable = !!details?.is_non_actionable;

    if (alreadyNonActionable) {
      return (
        <Tooltip title="Mark as actionable" arrow placement="top">
          <button
            aria-label="Mark as actionable"
            style={{
              background: "none",
              border: "none",
              padding: "4px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              ...extraStyle,
            }}
            onClick={(e) => {
              e.preventDefault();
              setConfirmPayload({
                id: details?.id,
                isNonActionable: details?.is_non_actionable,
              });
              setConfirmModalOpen(true);
            }}
          >
            <i
              className="bi bi-envelope-check"
              style={{ fontSize: "18px", color: "#6B7280" }}
            />
          </button>
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Mark as non-actionable" arrow placement="top">
        <button
          aria-label="Mark as non-actionable"
          style={{
            background: "none",
            border: "none",
            padding: "4px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            ...extraStyle,
          }}
          onClick={(e) => {
            e.preventDefault();
            setConfirmPayload({
              id: details?.id,
              isNonActionable: details?.is_non_actionable,
            });
            setConfirmModalOpen(true);
          }}
        >
          <i
            className="bi bi-envelope-slash"
            style={{ fontSize: "18px", color: "#6B7280" }}
          />
        </button>
      </Tooltip>
    );
  };

  const renderMobileView = () => (
    <>
      <div className="email-list-mobile">
        {allThreads.map((details: any) => {
          const avatarColor = getAvatarColor(
            details?.action?.contact?.name || "U",
          );
          const latestEmail =
            details?.action?.thread?.emails?.[
              details?.action?.thread?.emails?.length - 1
            ];
          const ticketId =
            details?.action?.ticket?.id != null
              ? String(details.action.ticket.id)
              : "";
          const subjectForAttr =
            (details?.subject != null
              ? decodeMimeWords(details.subject)
              : ""
            )?.slice(0, 200) ?? "";

          return (
            <div
              key={details?.id}
              className="email-card-mobile"
              data-email-ticket-id={ticketId || undefined}
              data-email-subject={subjectForAttr || undefined}
            >
              <div
                className={`email-card-mobile__status ${
                  details?.is_new
                    ? "email-card-mobile__status--green"
                    : "email-card-mobile__status--orange"
                }`}
              />

              <div className="email-card-mobile__content">
                <div className="email-card-mobile__top">
                  <input
                    type="checkbox"
                    className="email-card-mobile__checkbox"
                    value={details?.id}
                    onChange={handleRowCheckboxChange}
                    checked={selectedRows.includes(String(details?.id))}
                    aria-label={`Select email from ${details?.action?.contact?.name}`}
                  />
                  <Link
                    to={`/email/inbox/${details?.id}`}
                    className="email-card-mobile__avatar-link"
                    onClick={() =>
                      saveReturnInfo(
                        details?.action?.ticket?.id,
                        details?.subject,
                      )
                    }
                  >
                    <div
                      className="email-card-mobile__avatar"
                      style={{
                        background: avatarColor.bg,
                        color: avatarColor.text,
                      }}
                    >
                      {getInitials(details?.action?.contact?.name)}
                    </div>
                  </Link>
                  <div className="email-card-mobile__sender-wrapper">
                    <Link
                      to={`/email/inbox/${details?.id}`}
                      className="email-card-mobile__sender-link"
                      onClick={() =>
                        saveReturnInfo(
                          details?.action?.ticket?.id,
                          details?.subject,
                        )
                      }
                    >
                      <div className="email-card-mobile__sender-name">
                        {capitalizeFirstLetter(details?.action?.contact?.name)}
                      </div>
                      {details?.action?.ticket?.id && (
                        <div className="email-card-mobile__ref-id">
                          {details.action.ticket.id}
                        </div>
                      )}
                    </Link>
                  </div>
                  <div className="email-card-mobile__date">
                    {mailSentOrReceivedDate(
                      latestEmail?.mail_sent_or_received_on,
                    )}
                  </div>
                </div>

                <Link
                  to={`/email/inbox/${details?.id}`}
                  className="email-card-mobile__body-link"
                  onClick={() =>
                    saveReturnInfo(
                      details?.action?.ticket?.id,
                      details?.subject,
                    )
                  }
                >
                  <div className="email-card-mobile__subject">
                    {decodeMimeWords(details?.subject) || "(No Subject)"}
                  </div>
                  <div className="email-card-mobile__preview">
                    {convertHtmlIntoText(latestEmail?.html_content)}
                  </div>
                </Link>

                <div className="email-card-mobile__bottom">
                  <span
                    className={`email-card-mobile__assignee ${
                      details?.action?.assignee?.username
                        ? "email-card-mobile__assignee--assigned"
                        : ""
                    }`}
                  >
                    {details?.action?.assignee?.username
                      ? capitalizeFirstLetter(details.action.assignee.username)
                      : "Unassigned"}
                  </span>
                  <div className="email-card-mobile__actions">
                    {/* Non-Actionable — disabled with slate style once marked */}
                    {renderNonActionableBtn(details, { fontSize: "18px" })}

                    <button
                      className="email-card-mobile__star"
                      onClick={(e) => {
                        e.preventDefault();
                        handleStarClick(details?.id, details?.is_starred);
                      }}
                      aria-label={
                        details?.is_starred ? "Unstar email" : "Star email"
                      }
                    >
                      <i
                        className={`bi bi-star${
                          details?.is_starred ? "-fill starred" : ""
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Link to="/email/compose" className="email-fab">
        <i className="bi bi-plus text-white"></i>
      </Link>
    </>
  );

  const renderDesktopView = () => (
    <table className="email-list-desktop">
      <tbody>
        {allThreads.map((details: any) => {
          const avatarColor = getAvatarColor(
            details?.action?.contact?.name || "U",
          );
          const latestEmail =
            details?.action?.thread?.emails?.[
              details?.action?.thread?.emails?.length - 1
            ];
          const ticketIdDesk =
            details?.action?.ticket?.id != null
              ? String(details.action.ticket.id)
              : "";
          const subjectForAttrDesk =
            (details?.subject != null
              ? decodeMimeWords(details.subject)
              : ""
            )?.slice(0, 200) ?? "";

          return (
            <tr
              key={details?.id}
              className="email-row-desktop"
              data-email-ticket-id={ticketIdDesk || undefined}
              data-email-subject={subjectForAttrDesk || undefined}
            >
              {/* Checkbox */}
              <td className="email-row-desktop__cell email-row-desktop__cell--checkbox">
                <input
                  type="checkbox"
                  className="email-row-desktop__checkbox"
                  value={details?.id}
                  onChange={handleRowCheckboxChange}
                  checked={selectedRows.includes(String(details?.id))}
                  aria-label={`Select email from ${details?.action?.contact?.name}`}
                />
              </td>

              {/* Star */}
              <td className="email-row-desktop__cell email-row-desktop__cell--star">
                <button
                  className="email-row-desktop__star"
                  onClick={() =>
                    handleStarClick(details?.id, details?.is_starred)
                  }
                  aria-label={
                    details?.is_starred ? "Unstar email" : "Star email"
                  }
                >
                  <i
                    className={`bi bi-star${
                      details?.is_starred ? "-fill starred" : ""
                    }`}
                  />
                </button>
              </td>

              {/* Non-Actionable — disabled with slate style once marked */}
              <td className="email-row-desktop__cell email-row-desktop__cell--non-actionable">
                {renderNonActionableBtn(details)}
              </td>

              {/* Status Bar */}
              <td className="email-row-desktop__cell email-row-desktop__cell--status">
                <div
                  className={`email-row-desktop__status ${
                    details?.is_new
                      ? "email-row-desktop__status--green"
                      : "email-row-desktop__status--orange"
                  }`}
                />
              </td>

              {/* Avatar */}
              <td className="email-row-desktop__cell email-row-desktop__cell--avatar">
                <Link
                  to={`/email/inbox/${details?.id}`}
                  className="email-row-desktop__avatar-link"
                  onClick={() =>
                    saveReturnInfo(
                      details?.action?.ticket?.id,
                      details?.subject,
                    )
                  }
                >
                  <div
                    className="email-row-desktop__avatar"
                    style={{
                      background: avatarColor.bg,
                      color: avatarColor.text,
                    }}
                  >
                    {getInitials(details?.action?.contact?.name)}
                  </div>
                </Link>
              </td>

              {/* Sender Name */}
              <td className="email-row-desktop__cell email-row-desktop__cell--sender">
                <Link
                  to={`/email/inbox/${details?.id}`}
                  className="email-row-desktop__sender-link"
                  onClick={() =>
                    saveReturnInfo(
                      details?.action?.ticket?.id,
                      details?.subject,
                    )
                  }
                >
                  <div className="email-row-desktop__sender-name">
                    {capitalizeFirstLetter(details?.action?.contact?.name)}
                  </div>
                  {details?.action?.ticket?.id && (
                    <div className="email-row-desktop__ref-id">
                      {details.action.ticket.id}
                    </div>
                  )}
                </Link>
              </td>

              {/* Subject & Preview */}
              <td className="email-row-desktop__cell email-row-desktop__cell--subject">
                <Link
                  to={`/email/inbox/${details?.id}`}
                  className="email-row-desktop__subject-link"
                  onClick={() =>
                    saveReturnInfo(
                      details?.action?.ticket?.id,
                      details?.subject,
                    )
                  }
                >
                  <span className="email-row-desktop__subject-text">
                    {decodeMimeWords(details?.subject) || "(No Subject)"}
                  </span>
                  <span className="email-row-desktop__separator"> - </span>
                  <span className="email-row-desktop__preview-text">
                    {convertHtmlIntoText(latestEmail?.html_content)}
                  </span>
                </Link>
              </td>

              {/* Assignee */}
              <td className="email-row-desktop__cell email-row-desktop__cell--assignee">
                <span
                  className={`email-row-desktop__assignee ${
                    details?.action?.assignee?.username
                      ? "email-row-desktop__assignee--assigned"
                      : ""
                  }`}
                >
                  {details?.action?.assignee?.username
                    ? capitalizeFirstLetter(details.action.assignee.username)
                    : "Unassigned"}
                </span>
              </td>

              {/* Date */}
              <td className="email-row-desktop__cell email-row-desktop__cell--date">
                <span className="email-row-desktop__date-text">
                  {mailSentOrReceivedDate(
                    latestEmail?.mail_sent_or_received_on,
                  )}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="email-thread-list">
      {/* Toolbar */}
      <div className="email-toolbar">
        <div className="email-toolbar__left">
          <input
            type="checkbox"
            className="email-toolbar__checkbox"
            onChange={handleHeaderCheckboxChange}
            checked={selectAll}
            aria-label="Select all emails"
          />
          <button
            className="email-toolbar__btn"
            onClick={handleRefreshClick}
            title="Refresh"
            aria-label="Refresh"
          >
            <i className="bi bi-arrow-clockwise" />
          </button>
          <button
            className="email-toolbar__btn"
            onClick={assigneBulkEmailHandler}
            title="Assign"
            aria-label="Assign"
          >
            <i className="bi bi-person" />
          </button>
        </div>
        <div className="email-toolbar__right">
          <div className="email-toolbar__select-wrapper">
            <FormControl fullWidth size="small">
              <InputLabel id="team-select-label">Select assignee</InputLabel>
              <Select
                labelId="team-select-label"
                id="team-select"
                value={selectedTeamId}
                onChange={handleTeamChange}
                label="Select assignee"
              >
                <MenuItem value="My Team">My Team</MenuItem>
                <MenuItem value={localStorage.getItem("User_Id")}>
                  Self
                </MenuItem>
              </Select>
            </FormControl>
          </div>
          <Button
            variant="outlined"
            onClick={openFilterModal}
            className="email-toolbar__filter-btn"
          >
            <i className="bi bi-funnel" style={{ color: "#424242" }} />
            <span>Search filter</span>
          </Button>
        </div>
      </div>

      {/* Email List */}
      <div className="email-list">
        {isLoading || isLoadingPageChange ? (
          <div className="email-loading">
            <LoadingSpinner />
          </div>
        ) : allThreads && allThreads.length > 0 ? (
          viewMode === "mobile" ? (
            renderMobileView()
          ) : (
            renderDesktopView()
          )
        ) : (
          <div className="email-empty">
            <i className="bi bi-inbox" />
            <span>No emails found</span>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="email-pagination">
        <div className="email-pagination__left">
          <span className="email-pagination__label">Show</span>
          <select
            className="email-pagination__select"
            value={pageSize}
            onChange={handlePageSizeChange}
            aria-label="Select page size"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="email-pagination__label">per page</span>
        </div>
        <div className="email-pagination__right">
          {totalPages > 0 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              sx={{
                "& .MuiPaginationItem-root": {
                  fontSize: "14px",
                  minWidth: "32px",
                  height: "32px",
                  margin: "0 2px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  color: "#374151",
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#6366f1",
                    color: "#ffffff",
                    borderColor: "#6366f1",
                    "&:hover": {
                      backgroundColor: "#4f46e5",
                      borderColor: "#4f46e5",
                    },
                  },
                },
              }}
              showFirstButton
              showLastButton
              siblingCount={0}
              boundaryCount={1}
              size="small"
              renderItem={(item) => <PaginationItem {...item} />}
            />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <ReactModal
        isOpen={isFilterModalOpen}
        onRequestClose={closeFilterModal}
        style={customFilterModalStyles}
        contentLabel="Filter Modal"
      >
        {filterKey === "selectStaredFilterAndValue1" ? (
          <FilterModalComponent
            closeFilterModal={closeFilterModal}
            selectStaredFilterAndValue={selectedFilter1}
            setSelectStaredFilterAndValue={setSelectedFilter1}
            handleSubmit={handleSubmit}
          />
        ) : filterKey === "sentSelectedFilter1" ? (
          <FilterModalComponent
            closeFilterModal={closeFilterModal}
            selectedSentFilterAndValue={selectedFilter1}
            setSelectedSentFilterAndValue={setSelectedFilter1}
            handleSubmit={handleSubmit}
          />
        ) : filterKey === "selectNonActionableFilterAndValue1" ? (
          <FilterModalComponent
            closeFilterModal={closeFilterModal}
            selectNonActionableFilterAndValue={selectedFilter1}
            setSelectNonActionableFilterAndValue={setSelectedFilter1}
            handleSubmit={handleSubmit}
          />
        ) : (
          <FilterModalComponent
            closeFilterModal={closeFilterModal}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            handleSubmit={handleSubmit}
          />
        )}
      </ReactModal>

      {/* Assign Modal */}
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={customModalStyles}
        contentLabel="Assign Modal"
        ariaHideApp={false}
      >
        <AssigneeBulkTeam
          closeModal={closeModal}
          setectedEmail={selectedRows}
          refetchData={() => setRefetch(true)}
          setSelectedRows={setSelectedRows}
        />
      </ReactModal>

      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setConfirmPayload(null);
        }}
        onConfirm={handleConfirmNonActionable}
        title="Confirm Action"
        message={
          confirmPayload?.isNonActionable ? (
            <>
              Are you sure you want to{" "}
              <strong>mark this email as actionable</strong>?
            </>
          ) : (
            <>
              Are you sure you want to{" "}
              <strong>mark this email as non-actionable</strong>?
            </>
          )
        }
        confirmLabel={"Confirm"}
        confirmButtonClass={"btn btn-primary"}
      />
    </div>
  );
};

export default EmailThreadList;
